import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "className", "disabled", "fwdRef", "isOpen", "label", "labelWhenPopupHintPassedToFormRowAsHint", "onChange", "placement", "popupHintProps", "screenReaderText"],
  _excluded2 = ["className"];
import React, { forwardRef, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { arrow, autoPlacement, autoUpdate, flip, FloatingArrow, FloatingFocusManager, limitShift, offset, shift, useClick, useDismiss, useFloating, useInteractions } from '@floating-ui/react';
import { v4 as uuidv4 } from 'uuid';
import { buildVersionedClass, buildVersionedClassNames } from '@op/opds.opux-utilities';
import { color } from '@op/opds.tokens';
import ScreenReaderOnly from '../screen-reader-only/screen-reader-only';
import { CloseButton } from './_closeButton';
import { TriggerButton } from './_triggerButton';
import { FormRowContext } from '../form-row/form-row';
import messages from './_messages';
import { useLocale } from '../common/locale';
/**
 * _A strictly specified component for displaying hint text bubbles e.g., next to form row labels._
 *
 * ***
 *
 * The `<PopupHint>` component use case is strictly specified for providing textual content in an
 * expandable floating bubble that is opened from a question mark icon button.
 *
 * The component essentially is an expander with the exception that the content is closed when the
 * focus is moved out of it.
 *
 * The component is visually closely similar to the [`PopupModal`](#/Layout%20%26%20Structure/PopupModal),
 * which is a focus capturing small dialog bubble.
 *
 * Additional props (including `ref`) will be forwarded to the wrapping `<div>` element.
 */
export const PopupHint = _ref => {
  let {
      children,
      className,
      disabled,
      fwdRef,
      isOpen = false,
      label,
      labelWhenPopupHintPassedToFormRowAsHint,
      onChange,
      placement = 'right',
      popupHintProps = {},
      screenReaderText = undefined
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const spanRef = useRef(null);
  const [controlledVisible, setControlledVisible] = useState(isOpen);
  const popupHintId = useMemo(() => "ds-ph-" + uuidv4(), []);
  const {
      className: hintPropsClassName
    } = popupHintProps,
    restPopupHintProps = _objectWithoutPropertiesLoose(popupHintProps, _excluded2);
  // placement prop doesn't have "auto" options in Floating UI anymore but the same functionality can be achieved with autoPlacement()
  const useAutoPlacement = placement.indexOf('auto') !== -1;
  const {
    formRowLabel
  } = useContext(FormRowContext);
  const lc = useLocale();

  // Hacky way to fix VO navigation problems with chrome.
  useEffect(() => {
    const bubbleContainer = refs.floating;
    if (bubbleContainer.current) {
      bubbleContainer.current.removeAttribute('tabindex');
    }
  });
  const labelIfPopupHintPassedAsHint = labelWhenPopupHintPassedToFormRowAsHint ? messages[lc].info + (": " + labelWhenPopupHintPassedToFormRowAsHint) : messages[lc].info;
  const defineLabel = formRowLabel ? messages[lc].info + (": " + formRowLabel) : labelIfPopupHintPassedAsHint;
  const iconLabel = label != null ? label : defineLabel;

  // https://floating-ui.com/docs/react
  const arrowRef = useRef(null);
  const {
    context,
    floatingStyles,
    refs
  } = useFloating({
    // https://floating-ui.com/docs/middleware#ordering
    // The order of options in the middleware array matters (for example if autoPlacement is used, it needs to be the first in the array to get the arrow to work correctly)
    // Also the same option can be in the array multiple times and the combination of them makes the tooltip behave a certain way
    // The options array below makes the arrow always point the reference element and the tooltip to position it automatically when scrolling the page (the shifts and flip)
    // flip() and autoPlacement() are mutually exclusive
    middleware: [useAutoPlacement && autoPlacement({
      alignment: placement.split('-')[1]
    }),
    // Valid alignment values are undefined, "start" or "end"
    shift(), arrow({
      element: arrowRef
    }), offset(8),
    // Magic number 8 is $opds-space-xss. @floating-ui treats values a pixels
    shift({
      limiter: limitShift()
    }), !useAutoPlacement && flip({
      fallbackAxisSideDirection: 'start'
    })],
    open: controlledVisible,
    onOpenChange: setControlledVisible,
    placement: useAutoPlacement ? undefined : placement,
    whileElementsMounted: autoUpdate // To keep the hint anchored to the reference element when rezising the window
  });
  const click = useClick(context);
  const dismiss = useDismiss(context);
  const {
    getFloatingProps,
    getReferenceProps
  } = useInteractions([click, dismiss]);
  useEffect(() => {
    if (onChange) {
      onChange(controlledVisible);
    }
  }, [controlledVisible, onChange]);
  useEffect(() => {
    setControlledVisible(isOpen);
  }, [isOpen]);
  return /*#__PURE__*/React.createElement("div", _extends({
    className: className,
    ref: fwdRef
  }, props), "\u200D", /*#__PURE__*/React.createElement(TriggerButton, _extends({
    "aria-expanded": controlledVisible,
    controlledVisible: controlledVisible,
    disabled: disabled,
    label: iconLabel,
    popupHintId: popupHintId,
    setReference: refs.setReference
  }, getReferenceProps())), controlledVisible && /*#__PURE__*/React.createElement(FloatingFocusManager, {
    context: context,
    modal: false,
    initialFocus: -1
  }, /*#__PURE__*/React.createElement("div", _extends({
    className: buildVersionedClassNames('ds-popup-hint', hintPropsClassName),
    id: popupHintId,
    ref: refs.setFloating,
    style: floatingStyles
  }, restPopupHintProps, getFloatingProps()), /*#__PURE__*/React.createElement(FloatingArrow, {
    context: context,
    fill: color.background.default,
    ref: arrowRef
  }), !!screenReaderText && /*#__PURE__*/React.createElement(ScreenReaderOnly, null, screenReaderText), /*#__PURE__*/React.createElement("span", {
    className: buildVersionedClass('ds-popup-hint__content'),
    "aria-hidden": screenReaderText ? 'true' : undefined,
    ref: spanRef
  }, children), /*#__PURE__*/React.createElement(CloseButton, {
    setControlledVisible: setControlledVisible
  }))));
};
const Fwd = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/React.createElement(PopupHint, _extends({}, props, {
  fwdRef: ref
})));
Fwd.displayName = 'PopupHint';
export default Fwd;